import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiX } = FiIcons;

const DeleteConfirmModal = ({ 
  title, 
  message, 
  onConfirm, 
  onCancel,
  confirmText = "Delete",
  confirmIcon = FiAlertTriangle,
  confirmColor = "bg-red-600 hover:bg-red-700"
}) => {
  const modalRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    // Prevent scrolling while modal is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [onCancel]);

  // Close on escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <motion.div
        ref={modalRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
      >
        <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900 flex items-center">
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 mr-2 text-amber-500" />
            {title}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-500"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 md:p-5">
          <p className="text-gray-700">{message}</p>
          <div className="mt-6 flex space-x-2 justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={`flex items-center space-x-2 px-4 py-2 text-white rounded-lg ${confirmColor}`}
            >
              <SafeIcon icon={confirmIcon} className="w-4 h-4" />
              <span>{confirmText}</span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DeleteConfirmModal;